import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { PlatformCard, StatusChip } from './PlatformCard';
import { EVENT_LABEL, errorMessage, formatDateTime, planLabel, platformApi } from './platformApi';

interface IGroup { _id: string | null; count: number }
interface IDashboard {
  total: number;
  byPlan: IGroup[];
  byStatus: IGroup[];
  expiring7: number;
  expiring30: number;
  posAccounts: number;
  recentEvents: any[];
}

const Stat: FC<{ label: string; value: number | string; to?: string }> = ({ label, value, to }) => (
  <Grid item xs={6} md={3}>
    <PlatformCard title={label}>
      <h2 style={{ margin: 0 }}>{to ? <Link to={to} style={{ color: '#059669', textDecoration: 'none' }}>{value}</Link> : value}</h2>
    </PlatformCard>
  </Grid>
);

// One "label ……… count" line in a dashboard card; the whole row opens the filtered accounts list
const CountRow: FC<{ to: string; count: number }> = ({ to, count, children }) => (
  <Link to={to} style={{ textDecoration: 'none' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 1, mx: -1, borderRadius: '6px', color: '#059669', fontSize: 14, '&:hover': { bgcolor: '#f3f4f6' }, '& + &': { borderTop: '1px solid #f0f1f3' } }}>
      <span>{children}</span>
      <b style={{ color: '#111827' }}>{count}</b>
    </Box>
  </Link>
);

const PlatformDashboard: FC = () => {
  const [data, setData] = useState<IDashboard>();
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    platformApi.get('/dashboard')
      .then((response) => { if (active) setData(response.data); })
      .catch((err) => { if (active) setError(errorMessage(err)); });
    return () => { active = false; };
  }, []);

  const notMigrated = data?.byPlan.find((g) => !g._id)?.count || 0;

  return (
    <>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {notMigrated > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {notMigrated} account(s) have no subscription record yet. Run the subscription migration to give them a plan and validity.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Stat label='Accounts' value={data?.total ?? '-'} to='/platform/accounts' />
        <Stat label='Expiring in 7 days' value={data?.expiring7 ?? '-'} to='/platform/accounts?expiringInDays=7' />
        <Stat label='Expiring in 30 days' value={data?.expiring30 ?? '-'} to='/platform/accounts?expiringInDays=30' />
        <Stat label='POS accounts' value={data?.posAccounts ?? '-'} />

        <Grid item xs={12} md={6}>
          <PlatformCard title='Accounts by plan' fill>
            {(data?.byPlan || []).map((g) => (
              <CountRow key={String(g._id)} to={`/platform/accounts?plan=${g._id || 'none'}`} count={g.count}>{planLabel(g._id)}</CountRow>
            ))}
            {data && !data.byPlan.length && <span style={{ color: '#6b7280', fontSize: 14 }}>No accounts.</span>}
          </PlatformCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <PlatformCard title='Accounts by status' fill>
            {(data?.byStatus || []).filter((g) => g._id).map((g) => (
              <CountRow key={String(g._id)} to={`/platform/accounts?status=${g._id}`} count={g.count}><StatusChip status={g._id} /></CountRow>
            ))}
            {data && !data.byStatus.some((g) => g._id) && <span style={{ color: '#6b7280', fontSize: 14 }}>No subscriptions yet.</span>}
          </PlatformCard>
        </Grid>

        <Grid item xs={12}>
          <PlatformCard title='Recent subscription activity'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.recentEvents || []).map((event) => (
                  <TableRow key={event._id}>
                    <TableCell>{formatDateTime(event.at)}</TableCell>
                    <TableCell>{event.account ? <Link to={`/platform/accounts/${event.account._id}`} style={{ color: '#059669', textDecoration: 'none' }}>{event.account.name}</Link> : '-'}</TableCell>
                    <TableCell>{EVENT_LABEL[event.type] || event.type}{event.product === 'pos' ? ' (POS)' : ''}</TableCell>
                    <TableCell>{event.actorName || 'System'}</TableCell>
                    <TableCell>{event.reason || '-'}</TableCell>
                  </TableRow>
                ))}
                {data && !data.recentEvents.length && <TableRow><TableCell colSpan={5}>No activity yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </PlatformCard>
        </Grid>
      </Grid>
    </>
  );
};

export default PlatformDashboard;
